import type { QueryClient } from '@tanstack/react-query';

import { fetchReferenceAssistantGateway } from './fetchReferenceAssistantGateway';
import {
  patchStatementReferenceState,
  putStatementReferenceState,
} from './statementReferenceCache';
import {
  planProvidedStatementReferences,
  type PublishProvidedStatementReferencesInput,
  type PublishProvidedStatementReferencesResult,
  StatementReferenceError,
} from '../model/providedReferences';
import type { ReferenceAssistantGateway } from '../ports/referenceAssistantGateway';

/** The cache a publish updates: the conversation's list for this account. */
export interface StatementReferenceCacheTarget {
  queryClient: QueryClient;
  userId: string | undefined;
}

/**
 * Publish a citation-wizard provide phase as one write, or nothing when the
 * plan is a no-op. Last write wins, as the plan's module notes.
 *
 * With a cache target, the bubble shows the write at once and takes the
 * server's state when it answers, or goes back to what it showed if the
 * write fails.
 */
export async function publishProvidedStatementReferences(
  input: PublishProvidedStatementReferencesInput,
  gateway: ReferenceAssistantGateway = fetchReferenceAssistantGateway,
  cache?: StatementReferenceCacheTarget
): Promise<PublishProvidedStatementReferencesResult> {
  const { patch, result } = planProvidedStatementReferences(input);

  if (!patch) {
    return result;
  }

  const rollback =
    cache &&
    patchStatementReferenceState(
      cache.queryClient,
      cache.userId,
      input.conversationId,
      input.messageSid,
      patch
    );

  try {
    const state = await gateway.updateStatementReferences(
      input.conversationId,
      input.messageSid,
      patch
    );

    if (cache) {
      putStatementReferenceState(cache.queryClient, cache.userId, input.conversationId, state);
    }
  } catch (error) {
    rollback?.();

    const message =
      error instanceof Error ? error.message : 'Unable to save references. Please try again.';

    throw new StatementReferenceError('persist_failed', message, error);
  }

  return result;
}
